// What an accident report needs before it can go to the insurer.
//
// The portal's first accident form asked for a date, a location and one line of
// description, with the other party optional. CHEEMAM2's 21 Sep report ("scratched a
// parked car while parking") came in with no time, no street number, no rego for the car
// he hit and no word on whether he left a note — none of which Allianz will do without,
// and the last of which the driver is required to do by law. So this is the one list of
// what a claim needs: the portal form asks for it, the Driver Accidents page shows what's
// missing, and the office can ask the driver for exactly those items.
//
// Everything new lives in record.meta.claim; the other party stays in meta.otherParty, and
// address/description stay in the record's own location/description columns. Pure ESM.

export const HIT_OPTIONS = [
  ['parked_car', 'A parked car (nobody in it)'],
  ['moving_vehicle', 'Another vehicle with a driver'],
  ['property', 'Property — fence, wall, pole, gate, building'],
  ['person', 'A person or cyclist'],
  ['nothing', 'Nothing else — only our van was damaged'],
];
export const hitLabel = (v) => (HIT_OPTIONS.find(([k]) => k === v) || [, ''])[1];

// kind: text · textarea · time · yesno · hit · photo
// where: claim (meta.claim[key]) · op (meta.otherParty[opKey]) · location · description · docs
export const ACCIDENT_FIELDS = [
  { key: 'time',        label: 'Time it happened',                 kind: 'time',     where: 'claim' },
  { key: 'address',     label: 'Full address incl. street number (or corner of two streets)', kind: 'text', where: 'location', placeholder: 'e.g. 42 Brougham Street, Potts Point — or Corner of Victoria St and Orwell St' },
  { key: 'description', label: 'What happened',                    kind: 'textarea', where: 'description' },
  { key: 'hit',         label: 'What was hit',                     kind: 'hit',      where: 'claim' },
  { key: 'vanRego',     label: 'Our van’s rego',                   kind: 'text',     where: 'claim', upper: true },
  { key: 'ourDamage',   label: 'Damage to our van',                kind: 'textarea', where: 'claim', placeholder: 'Where on the van, and how bad' },
  { key: 'theirDamage', label: 'Damage to the other vehicle / property', kind: 'textarea', where: 'claim' },
  { key: 'otherRego',   label: 'Other vehicle’s rego',             kind: 'text',     where: 'op', opKey: 'rego', upper: true, placeholder: 'Type UNKNOWN if it drove off' },
  { key: 'platePhoto',  label: 'Photo of their number plate',      kind: 'photo',    where: 'docs', docLabel: 'Number plate' },
  { key: 'noteLeft',    label: 'Did you leave a note with your details?', kind: 'yesno', where: 'claim' },
  { key: 'otherName',   label: 'Other driver’s name',              kind: 'text',     where: 'op', opKey: 'name' },
  { key: 'otherPhone',  label: 'Other driver’s phone',             kind: 'text',     where: 'op', opKey: 'phone' },
  { key: 'otherLicence', label: 'Other driver’s licence #',        kind: 'text',     where: 'op', opKey: 'licence' },
  { key: 'otherInsurer', label: 'Other driver’s insurer',          kind: 'text',     where: 'op', opKey: 'insurer' },
  { key: 'injuries',    label: 'Was anyone hurt?',                 kind: 'yesno',    where: 'claim' },
  { key: 'police',      label: 'Were police told? (event # if yes)', kind: 'text',   where: 'claim', placeholder: 'No — or Yes, event E12345678' },
  { key: 'witnesses',   label: 'Witnesses (name + phone)',         kind: 'textarea', where: 'claim', placeholder: 'None — or name and number' },
  { key: 'photos',      label: 'Photos of the damage',             kind: 'photo',    where: 'docs', docLabel: 'Damage' },
];
export const fieldByKey = (k) => ACCIDENT_FIELDS.find((f) => f.key === k);

const s = (v) => (v == null ? '' : v.toString().trim());
const docs = (r) => (Array.isArray(r && r.documents) ? r.documents : []);

// The stored value of one field on a record ('' when not given).
export function valueOf(r, key) {
  const f = fieldByKey(key);
  if (!f || !r) return '';
  const meta = r.meta || {};
  if (f.where === 'claim') return s((meta.claim || {})[key]);
  if (f.where === 'op') return s((meta.otherParty || {})[f.opKey]);
  if (f.where === 'location') return s(r.location);
  if (f.where === 'description') return s(r.description);
  if (key === 'platePhoto') return docs(r).some((d) => /plate/i.test(d.label || '')) ? 'yes' : '';
  if (key === 'photos') return (docs(r).length || r.viewUrl || r.fileId) ? 'yes' : '';
  return '';
}

// An address an insurer can find: a street number, or — when it happened at an intersection,
// where there is no number — the two streets that meet ("Corner of Victoria St and Orwell St",
// "Intersection of X & Y", "Cnr X / Y"). Jayda hit this on 29 Oct 2026: a crash at a corner
// couldn't be saved because the old check wanted a digit.
const CORNER = /\b(corner|cnr|intersection|junction|crn)\b/i;
export function addressOk(v) {
  const a = s(v);
  if (!a) return false;
  if (/\d/.test(a)) return true;
  if (!CORNER.test(a)) return false;
  // Two streets named: something either side of "and" / "&" / "/" / "," once the corner word is gone.
  const rest = a.replace(CORNER, ' ').replace(/\bof\b/i, ' ');
  const parts = rest.split(/\s*(?:\band\b|&|\/|,|\+)\s*/i).filter((x) => /[a-z]{2,}/i.test(x));
  return parts.length >= 2;
}

const VEHICLE = new Set(['parked_car', 'moving_vehicle']);

// The fields a claim still needs, in form order. Which ones apply depends on what was hit;
// when that isn't known yet the vehicle questions are asked too — most of these are
// vehicles, and the office can untick any that don't apply before sending the request.
export function missingForClaim(r) {
  const hit = valueOf(r, 'hit');
  const vehicle = !hit || VEHICLE.has(hit);
  const need = ['time', 'address', 'description', 'hit', 'vanRego', 'ourDamage', 'injuries', 'police', 'photos'];
  if (hit !== 'nothing') need.push('theirDamage');
  // A rego of UNKNOWN (the other car drove off) is an answer — there's no plate to photograph
  // and no driver to take details from.
  const unknown = /^unknown$/i.test(valueOf(r, 'otherRego'));
  if (vehicle) need.push('otherRego');
  if (vehicle && !unknown) need.push('platePhoto');
  if (!hit || hit === 'parked_car') need.push('noteLeft');
  if (hit === 'moving_vehicle' && !unknown) need.push('otherName', 'otherPhone', 'otherLicence', 'otherInsurer');
  const order = ACCIDENT_FIELDS.map((f) => f.key);
  return need
    .filter((k) => {
      const v = valueOf(r, k);
      // An address with no street number (and no corner) is as good as none for an insurer.
      if (k === 'address') return !addressOk(v);
      return !v;
    })
    .sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

// The office's open request on a record, if any.
export const openRequest = (r) => {
  const q = r && r.meta && r.meta.infoRequest;
  return q && q.status === 'pending' ? q : null;
};

// A record-shaped object from what's been typed into a form, so a form can be checked with
// missingForClaim before it's saved. photos/plate are counts of pages picked.
export const draftRecord = ({ location, description, claim, op, photos = 0, plate = 0, base }) => {
  const b = base || {};
  const m = b.meta || {};
  const pick = (a, c) => (s(a) ? a : c);
  const claimOut = { ...(m.claim || {}) };
  Object.entries(claim || {}).forEach(([k, v]) => { if (s(v)) claimOut[k] = v; });
  const opOut = { ...(m.otherParty || {}) };
  Object.entries(op || {}).forEach(([k, v]) => { if (s(v)) opOut[k] = v; });
  return {
    location: pick(location, b.location), description: pick(description, b.description),
    viewUrl: b.viewUrl, fileId: b.fileId,
    documents: [...docs(b), ...Array(photos).fill({ label: 'Damage' }), ...Array(plate).fill({ label: 'Number plate' })],
    meta: { ...m, claim: claimOut, otherParty: opOut },
  };
};
